package com.elan.edubridge.data.firebase

import com.elan.edubridge.data.firebase.externals.Functions
import com.elan.edubridge.data.firebase.externals.httpsCallable
import com.elan.edubridge.shared.data.firebase.FirebaseNaming
import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.AuthResult
import kotlin.js.json
import kotlinx.coroutines.await

/**
 * Google 로그인 + 플랫폼 관리자 부트스트랩의 웹 진입점. 로그인 자체는 shared 계약
 * (`AccountRepository.signInWithGoogle`)이 담당하고, 이 object는 웹에만 있는 부트스트랩 단계를
 * 덧붙인다 — UI(`AuthPage.kt`)는 이 object를 호출한다.
 *
 * [configure]는 [installWebGraph]가 `SharedGraph.install()` 직후 정확히 한 번 호출한다.
 * `Main.kt`가 항상 `installWebGraph()`를 먼저 부르므로 [signInWithGoogle] 호출 시점에는
 * 이미 배선이 끝나 있다고 가정한다(WebGraph.kt의 기존 호출 순서 전제와 동일).
 */
object WebAuth {
    private lateinit var functions: Functions
    private val accountRepository by lazy { SharedGraph.createAccountRepository() }

    fun configure(functions: Functions) {
        this.functions = functions
    }

    /**
     * Google 팝업 로그인 + 플랫폼 관리자 부트스트랩.
     *
     * 웹은 팝업이 토큰 교환까지 처리하므로 계약에 `idToken = null`을 넘긴다(Android는 Credential
     * Manager로 먼저 받은 토큰을 넘긴다). 회원가입 화면에서만 [registrationName]과 [birthDate]를
     * 함께 넘겨 프로필이 없는 Google 계정의 생성에 쓴다. 세션 발행은 `AccountRepositoryImpl`이
     * login()/register()와 동일하게 처리하므로 예전처럼 `restoreSession()`을 덧붙일 필요가 없다.
     *
     * 그 다음 `edubridge-claimPlatformAdmin`을 호출한다. 비허용자는 에러가 아니라 `granted:false`
     * 정상 응답이므로(서버 계약) UI에 오류를 보이지 않는다. `granted:true`일 때만 ID 토큰을 강제
     * 갱신하고 세션을 다시 읽는다 — custom claim은 최대 1시간 캐시되어 갱신 없이는 superAdmin
     * 권한이 반영되지 않는다(R11). 콜러블 자체가 실패(네트워크 등)해도 로그인은 이미 끝났으므로
     * 막지 않고 조용히 넘어간다.
     */
    suspend fun signInWithGoogle(registrationName: String?, birthDate: String?): AuthResult {
        val result = accountRepository.signInWithGoogle(
            idToken = null,
            registrationName = registrationName,
            birthDate = birthDate,
        )
        if (result.account != null) {
            claimPlatformAdminIfEligible()
        }
        return result
    }

    private suspend fun claimPlatformAdminIfEligible() {
        try {
            val callableName = "${FirebaseNaming.FUNCTIONS_PREFIX}claimPlatformAdmin"
            val response = httpsCallable(functions, callableName)(json()).await()
            val data = response.data
            val granted = js("data != null && data.granted === true") as Boolean
            if (granted) {
                accountRepository.forceRefreshToken()
            }
        } catch (e: Throwable) {
            // 네트워크 실패 등 — 로그인 자체는 이미 끝났으므로 막지 않고 조용히 무시한다.
        }
    }
}
